import { createContext, FC, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import API from '../api/api';
import {
    EPhoneNumberVerificationType,
    EQueryStringParam,
    EStaffAccountType,
    maxStaffFirstNameLength,
    maxStaffLastNameLength,
} from '../util/constants';
import GlobalContext from './GlobalContext';
import { validatePassword } from '../util/validation/passwordValidation';
import {
    TCompleteConfirmStaffAccountPost,
    TCompleteConfirmStaffPhoneNumberPost,
    TRequestConfirmStaffAccountPost,
    TStartConfirmStaffPhoneNumberPost,
} from '../api/types';
import formatPhoneNumber from 'phone';
import { replacePhrase } from '../util/helpers';

type TConfirmStaffAccountContext = {
    confirmStaffAccountUiState: EConfirmStaffAccountUiState;
    firstName: string;
    setFirstName: (firstName: string) => void;
    firstNameIsValid: boolean;
    firstNameValidationMessage: string;
    lastName: string;
    setLastName: (lastName: string) => void;
    lastNameIsValid: boolean;
    lastNameValidationMessage: string;
    email: string;
    phoneNumber: string;
    setPhoneNumber: (phoneNumber: string) => void;
    phoneNumberVerificationType: EPhoneNumberVerificationType;
    setPhoneNumberVerificationType: (type: EPhoneNumberVerificationType) => void;
    phoneNumberVerificationCode: string;
    setPhoneNumberVerificationCode: (code: string) => void;
    phoneNumberIsValid: boolean;
    password: string;
    setPassword: (password: string) => void;
    passwordValidationIsActive: boolean;
    countryCode: string;
    setCountryCode: (countryCode: string) => void;
    disableSubmit: boolean;
    submitConfirmAccount: () => Promise<void>;
    validateAndSubmitPhoneNumber: () => void;
    submitPhoneNumber: () => Promise<void>;
    submitVerifyPhoneNumber: () => Promise<void>;
    skipVerifyPhoneNumber: () => void;
    resendOnboardingEmail: () => Promise<void>;
};

export enum EConfirmStaffAccountUiState {
    default,
    queryStringParsed,
    ready,
    accountExists,
    loading,
    accountCreated,
    phoneVerification,
    success,
    expired,
    softError,
    fatalError,
}

const ConfirmStaffAccountContext = createContext<TConfirmStaffAccountContext | undefined>(undefined);

export const ConfirmStaffAccountProvider: FC = (props) => {
    const {
        language,
        gatewayAPIBaseUrl,
        countriesList,
        getCountriesList,
        coBranding,
        setCoBranding,
        coBrandingChecked,
        getCoBranding,
        setStaffAccountType,
        setSubscriberId,
        customerAccountId,
        setCustomerAccountId,
        csrfToken,
        getCsrfToken,
        subscriberId,
    } = useContext(GlobalContext);
    const [confirmStaffAccountUiState, setConfirmStaffAccountUiState] = useState(EConfirmStaffAccountUiState.default);
    const [userId, setUserId] = useState('');
    const [code, setCode] = useState('');
    const [firstName, setFirstName] = useState('');
    const [firstNameIsValid, setFirstNameIsValid] = useState(true);
    const [firstNameValidationMessage, setFirstNameValidationMessage] = useState('');
    const [lastName, setLastName] = useState('');
    const [lastNameIsValid, setLastNameIsValid] = useState(true);
    const [lastNameValidationMessage, setLastNameValidationMessage] = useState('');
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [phoneNumberIsValid, setPhoneNumberIsValid] = useState(true);
    const [phoneNumberVerificationType, setPhoneNumberVerificationType] = useState(EPhoneNumberVerificationType.text);
    const [phoneNumberVerificationCode, setPhoneNumberVerificationCode] = useState('');
    const [password, setPassword] = useState('');
    const [passwordValidationIsActive, setPasswordValidationIsActive] = useState(false);
    const [passwordIsValid, setPasswordIsValid] = useState(false);
    const [countryCode, setCountryCode] = useState('');
    const [disableSubmit, setDisableSubmit] = useState(true);
    const { search: queryParams } = useLocation();
    const formattedPhoneNumber = useMemo(() => formatPhoneNumber(phoneNumber), [phoneNumber]);
    const formattedFirstNameMaxLength = useMemo(() => {
        const desc1 = replacePhrase(language.validation.maxLength, '<noun>', language.common.firstName);
        const desc2 = replacePhrase(desc1, '<length>', maxStaffFirstNameLength.toString());
        return desc2;
    }, [language, maxStaffFirstNameLength]);
    const formattedLastNameMaxLength = useMemo(() => {
        const desc1 = replacePhrase(language.validation.maxLength, '<noun>', language.common.lastName);
        const desc2 = replacePhrase(desc1, '<length>', maxStaffLastNameLength.toString());
        return desc2;
    }, [language, maxStaffLastNameLength]);

    ////////////////
    // Setup
    ////////////////
    const parseQueryString = () => {
        const params = new URLSearchParams(queryParams);
        const userId = params.get(EQueryStringParam.userId);
        const code = params.get(EQueryStringParam.code);
        const customerAccountName = params.get(EQueryStringParam.customerAccountName);
        const customerAccountId = params.get(EQueryStringParam.customerAccountId);
        const subscriberId = params.get(EQueryStringParam.subscriberId);
        userId && setUserId(userId);
        code && setCode(code);
        subscriberId && setSubscriberId(subscriberId);
        customerAccountName && setCoBranding({ ...coBranding, name: customerAccountName });
        customerAccountId && setCustomerAccountId(customerAccountId);
        if (!code || !userId) {
            console.error(
                `Missing query string parameter(s) -- ${EQueryStringParam.code}: ${code} ${EQueryStringParam.userId} : ${userId}`,
            );
            setConfirmStaffAccountUiState(EConfirmStaffAccountUiState.fatalError);
        } else {
            setConfirmStaffAccountUiState(EConfirmStaffAccountUiState.queryStringParsed);
        }
    };

    const getInitialData = async () => {
        !countriesList.length && (await getCountriesList());
        !coBrandingChecked && (await getCoBranding());
        try {
            const response = await API.get.startConfirmStaffAccount(queryParams);
            const { userId, firstName, lastName, email, phoneNumber } = response.data;
            setUserId(userId);
            setFirstName(firstName);
            setLastName(lastName);
            setEmail(email);
            phoneNumber && setPhoneNumber(phoneNumber);
            setConfirmStaffAccountUiState(EConfirmStaffAccountUiState.ready);
        } catch (error) {
            if (error.response?.status === 409) {
                setConfirmStaffAccountUiState(EConfirmStaffAccountUiState.accountExists);
            } else if (error.response?.status === 400) {
                setConfirmStaffAccountUiState(EConfirmStaffAccountUiState.expired);
            } else {
                console.error('Failed to start confirming staff account');
                setConfirmStaffAccountUiState(EConfirmStaffAccountUiState.fatalError);
            }
        }
    };

    useEffect(() => {
        switch (confirmStaffAccountUiState) {
            case EConfirmStaffAccountUiState.default:
                parseQueryString();
                break;
            case EConfirmStaffAccountUiState.queryStringParsed:
                getInitialData();
                break;
            default:
                break;
        }
    }, [confirmStaffAccountUiState]);

    ////////////////
    // Validation
    ////////////////
    const checkFirstNameIsValid = () => {
        let isValid = true;
        let validationMessage = '';
        if (!firstName.length) {
            validationMessage = language.validation.firstName;
            isValid = false;
        } else if (firstName.length > maxStaffFirstNameLength) {
            validationMessage = formattedFirstNameMaxLength;
            isValid = false;
        }
        setFirstNameIsValid(isValid);
        setFirstNameValidationMessage(validationMessage);
    };

    const checkLastNameIsValid = () => {
        let isValid = true;
        let validationMessage = '';
        if (!lastName.length) {
            validationMessage = language.validation.lastName;
            isValid = false;
        } else if (lastName.length > maxStaffLastNameLength) {
            validationMessage = formattedLastNameMaxLength;
            isValid = false;
        }
        setLastNameIsValid(isValid);
        setLastNameValidationMessage(validationMessage);
    };

    const checkDisableSubmit = () => {
        if (firstNameIsValid && lastNameIsValid && passwordIsValid) {
            setDisableSubmit(false);
        } else {
            setDisableSubmit(true);
        }
    };

    useEffect(() => {
        checkFirstNameIsValid();
    }, [firstName]);

    useEffect(() => {
        checkLastNameIsValid();
    }, [lastName]);

    useEffect(() => {
        if (password.length && !passwordValidationIsActive) {
            setPasswordValidationIsActive(true);
        }
        const isValid = validatePassword(password);
        setPasswordIsValid(isValid);
    }, [password]);

    useEffect(() => {
        checkDisableSubmit();
    }, [firstNameIsValid, lastNameIsValid, passwordIsValid]);

    ////////////////
    // Submit Handlers
    ////////////////
    const submitConfirmAccount = async () => {
        const postObject: TCompleteConfirmStaffAccountPost = {
            code,
            firstName,
            lastName,
            password,
            countryCode,
            ...(Number(subscriberId) ? { subscriberId: Number(subscriberId) } : {}),
        };

        try {
            setConfirmStaffAccountUiState(EConfirmStaffAccountUiState.loading);
            await API.post.completeConfirmStaffAccount(userId, postObject);
            // Temporarily skip phone number validation (WI 1043051)
            // await getCsrfToken();
            setConfirmStaffAccountUiState(EConfirmStaffAccountUiState.success);
        } catch {
            console.error('Failed to confirm staff account');
            setConfirmStaffAccountUiState(EConfirmStaffAccountUiState.fatalError);
        }
    };

    const validateAndSubmitPhoneNumber = () => {
        if (formattedPhoneNumber.length) {
            setPhoneNumberIsValid(true);
            submitPhoneNumber();
        } else {
            setPhoneNumberIsValid(false);
        }
    };

    const submitPhoneNumber = async () => {
        const postObject: TStartConfirmStaffPhoneNumberPost = {
            phoneNumber: formattedPhoneNumber[0],
            channel: phoneNumberVerificationType,
        };
        try {
            setConfirmStaffAccountUiState(EConfirmStaffAccountUiState.loading);
            await API.post.startConfirmStaffPhoneNumber(postObject, csrfToken);
            // csrf token needed for next call in workflow
            await getCsrfToken();
            setConfirmStaffAccountUiState(EConfirmStaffAccountUiState.phoneVerification);
        } catch {
            console.error('Failed to send phone verification code');
            setConfirmStaffAccountUiState(EConfirmStaffAccountUiState.fatalError);
        }
    };

    const submitVerifyPhoneNumber = async () => {
        const postObject: TCompleteConfirmStaffPhoneNumberPost = {
            verificationCode: phoneNumberVerificationCode,
        };
        try {
            setConfirmStaffAccountUiState(EConfirmStaffAccountUiState.loading);
            await API.post.completeConfirmStaffPhoneNumber(postObject, csrfToken);
            setConfirmStaffAccountUiState(EConfirmStaffAccountUiState.success);
        } catch (error) {
            if (error.response && error.response.status === 400) {
                getCsrfToken();
                setConfirmStaffAccountUiState(EConfirmStaffAccountUiState.softError);
            } else {
                console.error('Failed to confirm phone number');
                setConfirmStaffAccountUiState(EConfirmStaffAccountUiState.fatalError);
            }
        }
    };

    const skipVerifyPhoneNumber = () => {
        setConfirmStaffAccountUiState(EConfirmStaffAccountUiState.success);
    };

    const resendOnboardingEmail = async () => {
        try {
            const postObject: TRequestConfirmStaffAccountPost = {
                customerAccountId,
                subscriberId: parseInt(subscriberId),
            };
            await API.post.requestConfirmStaffAccount(gatewayAPIBaseUrl, userId, postObject);
        } catch (error) {
            throw Error('Failed to resend onboarding email', { cause: error.response?.status });
        }
    };
    ////////////////
    // Country Code
    ////////////////
    useEffect(() => {
        if (!countryCode && countriesList.length) {
            setCountryCode(countriesList[0].code);
        }
    }, [countriesList]);

    ////////////////
    // Set Staff Account Type
    ////////////////
    useEffect(() => {
        setStaffAccountType(EStaffAccountType.account);
    }, []);

    return (
        <ConfirmStaffAccountContext.Provider
            value={{
                confirmStaffAccountUiState,
                firstName,
                setFirstName,
                firstNameIsValid,
                firstNameValidationMessage,
                lastName,
                setLastName,
                lastNameIsValid,
                lastNameValidationMessage,
                email,
                phoneNumber,
                setPhoneNumber,
                phoneNumberVerificationType,
                setPhoneNumberVerificationType,
                phoneNumberVerificationCode,
                setPhoneNumberVerificationCode,
                phoneNumberIsValid,
                password,
                setPassword,
                passwordValidationIsActive,
                countryCode,
                setCountryCode,
                disableSubmit,
                submitConfirmAccount,
                validateAndSubmitPhoneNumber,
                submitPhoneNumber,
                submitVerifyPhoneNumber,
                skipVerifyPhoneNumber,
                resendOnboardingEmail,
            }}
        >
            {props.children}
        </ConfirmStaffAccountContext.Provider>
    );
};

export default ConfirmStaffAccountContext;
